import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faCircle } from '@fortawesome/free-solid-svg-icons/faCircle';
import './RenewalsQuote.scss';
import Switch from '@mui/material/Switch';
import {
  KeyBenefitItem,
  KeyBenefitList,
  LinkedProduct,
  ProductVariant,
  SelectedLinkedProduct
} from '../../../common/CommonTypes';
import {
  getInstalmentPrice,
  getLinkedProductNames,
  getVariantInstalmentTotalPrice,
  getVariantTotalPrice,
  InstallmentPrice
} from '../../../common/ProductMethods';
import { PaymentMethod } from '../../../common/Constants';
import PaymentDisplayManager from './PaymentDisplayManager';
import { ButtonCommand } from '../../../common/Button/ButtonCommand';
import { formatNumber, getCurrencySymbol } from '../../../common/Formatter';
import { CommonValidation } from '../../../common/CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';

type RenewalsQuoteCardProps = {
  id: string;
  productId: number;
  keyBenefitLists: KeyBenefitList[];
  quoteTitle: string;
  priceMonthly: InstallmentPrice | undefined;
  priceSingle: number | undefined;
  priceSubscription: number | undefined;
  isPaymentDone: boolean;
  showMoreInfo: boolean;
  showSelectOption: boolean;
  priceTotal?: number;
  quoteType: number;
  selectedQuote?: number;
  noOfInstallments: number;
  linkedQuotes: LinkedProduct[];
  hideBenefits?: boolean;
  defaultPaymentMethod?: string;
  selectedOptionalLinkedQuotes?: SelectedLinkedProduct[];
  productVariants: ProductVariant[];
  showAllPricingOptions: boolean;
  isVariablePrice: boolean;
  onSelectionChange?: (value: number, type: number) => void;
  onLinkedOptionalQuoteSelectionChange?: (
    quoteCardId: number,
    linkedProductTypeId: number
  ) => void;
  culture: string;
};

const monthlyInstallmentPlanText = `Monthly instalment plan`;
const subscriptionPlanText = `Subscription plan`;
const singlePayText = `Single payment plan`;
const RenewalsQuoteCard = ({
  id,
  productId,
  keyBenefitLists,
  quoteTitle,
  priceMonthly,
  priceSingle,
  priceSubscription,
  isPaymentDone,
  showMoreInfo,
  showSelectOption,
  priceTotal,
  quoteType,
  selectedQuote,
  noOfInstallments,
  linkedQuotes,
  hideBenefits,
  defaultPaymentMethod,
  selectedOptionalLinkedQuotes,
  productVariants,
  showAllPricingOptions,
  isVariablePrice,
  onSelectionChange,
  onLinkedOptionalQuoteSelectionChange,
  culture
}: RenewalsQuoteCardProps): JSX.Element => {
  const { t } = useTranslation();
  const paymentMethodSortOrder = ['Single', 'Monthly', 'Subscription'];
  const [showAllBenefits, setShowAllBenefits] =
    useState<boolean>(!showMoreInfo);
  const currencySymbol = getCurrencySymbol();

  const onShowMore = () => {
    setShowAllBenefits(!showAllBenefits);
  };

  const onChange = (event: React.MouseEvent<HTMLDivElement>, value: number) => {
    if (onSelectionChange) {
      if (isSelectedQuote()) {
        if (!(event.target instanceof HTMLInputElement)) {
          onSelectionChange(0, 0);
        }
      } else {
        onSelectionChange(value, quoteType || 0);
      }
    }
  };

  const onLinkedOptionalQuoteChange = (
    quoteCardId: number,
    linkedQuoteTypeId: number
  ) => {
    if (!onLinkedOptionalQuoteSelectionChange) return;

    onLinkedOptionalQuoteSelectionChange(quoteCardId, linkedQuoteTypeId);
  };

  const isLinkedOptionalQuoteSelected = (
    quoteCardId: number,
    linkedQuoteTypeId: number
  ): boolean => {
    const result =
      selectedOptionalLinkedQuotes &&
      selectedOptionalLinkedQuotes.some(
        (slp) =>
          slp.quoteCardId === quoteCardId &&
          slp.linkedProductTypeId === linkedQuoteTypeId
      );

    return result === undefined ? false : result;
  };

  const showMoreBtnLabel = showAllBenefits
    ? t('renewals-quote.show-more-btn-label-show-less')
    : t('renewals-quote.show-more-btn-label-more-information');
  const subscriptionPerMonthText = t(
    'renewals-quote.subscription-per-month-text'
  );
  const recurringPaymentText = t('renewals-quote.recurring-payment-text');

  const optionalQuotes = linkedQuotes?.filter(
    (q) => q.isMandatory === false && q.paymentMethod === PaymentMethod.Monthly
  );

  //only showing for mandatory linked products now
  const showPaymentOptions =
    showAllPricingOptions && !(optionalQuotes && optionalQuotes.length > 0);

  const renderProductBenefit = (
    productBenefits: KeyBenefitList,
    showLess: boolean
  ) => {
    let benefitsList = productBenefits.keyBenefits;

    if (showLess) {
      benefitsList = benefitsList.slice(0, 2);
    }

    return (
      <>
        <p className='bold m1-bottom'>{`${productBenefits.title}:`}</p>
        {benefitsList.map((benefit: KeyBenefitItem, i) => (
          <div className='m1-bottom flex-row' key={`benefit-${productId}-${i}`}>
            <div className='flex-column auto-size-column'>
              <FontAwesomeIcon icon={faCheck} className='tick' />
            </div>
            <div className='flex-column'>
              <span className='m05-left'>{benefit.text}</span>
            </div>
          </div>
        ))}
      </>
    );
  };

  const renderBenefitSections = () => {
    if (!keyBenefitLists || keyBenefitLists.length === 0) {
      return <></>;
    }

    if (!showAllBenefits) {
      return renderProductBenefit(keyBenefitLists[0], true);
    }

    return keyBenefitLists.map((keyBenefitList, i) => (
      <div key={`benefit-section-${productId}-${i}`}>
        {renderProductBenefit(keyBenefitList, false)}
      </div>
    ));
  };

  const renderPrice = () => {
    if (isPaymentDone === true) {
      return (
        <>
          {priceSingle && (
            <p className='p1-bold'>
              {t('renewals-quote.amount-paid-text', {
                priceSingle: formatNumber(priceSingle, 2, culture),
                currencySymbol: currencySymbol
              })}
            </p>
          )}

          {priceSubscription && (
            <>
              <p className='p1-bold'>
                {currencySymbol}
                {formatNumber(priceSubscription, 2, culture)}
                {subscriptionPerMonthText}
              </p>
              <p>{recurringPaymentText}</p>
            </>
          )}

          {priceMonthly && (
            <>
              {getMonthyPriceForPaymentCompleted()}
              <p className='label'>
                {t('renewals-quote.total-amount-text', {
                  currencySymbol: currencySymbol,
                  priceTotal: formatNumber(priceTotal, 2, culture)
                })}
              </p>
            </>
          )}
        </>
      );
    }

    return (
      <>
        <PaymentDisplayManager
          paymentMethod={defaultPaymentMethod || PaymentMethod.Monthly}
          priceMonthly={priceMonthly}
          priceSingle={priceSingle}
          priceSubscription={priceSubscription}
          culture={culture}
        />
      </>
    );
  };

  const getMonthyPriceForPaymentCompleted = () => {
    if (!priceMonthly) {
      return <></>;
    }

    if (
      priceMonthly.firstInstallmentPrice ===
      priceMonthly.subsequentInstallmentPrice
    ) {
      return (
        <p className='p1-bold'>
          {t('renewals-quote.monthly-price-payment-completed', {
            currencySymbol: currencySymbol,
            firstInstallment: formatNumber(
              priceMonthly.firstInstallmentPrice,
              2,
              culture
            ),
            noOfInstallments: noOfInstallments
          })}
        </p>
      );
    } else {
      return renderInEqualMonthlyPrice();
    }
  };

  const renderInEqualMonthlyPrice = () => {
    return priceMonthly ? (
      <p className='p1-bold'>
        {t('renewals-quote.inequal-payment-text', {
          currencySymbol: currencySymbol,
          firstInstallment: formatNumber(
            priceMonthly.firstInstallmentPrice,
            2,
            culture
          ),
          subsequentPrice: formatNumber(
            priceMonthly.subsequentInstallmentPrice,
            2,
            culture
          ),
          noOfInstallments: noOfInstallments - 1
        })}{' '}
      </p>
    ) : (
      <></>
    );
  };

  const renderMandatoryQuoteIncludedText = () => {
    const result = getLinkedProductNames(
      productId,
      linkedQuotes,
      selectedOptionalLinkedQuotes || []
    );

    if (CommonValidation.isNotEmpty(result)) {
      return (
        <p className='p2'>
          {t('renewals-quote.mandatory-quotes-text', {
            productName: result
          })}
        </p>
      );
    }
  };

  const renderOptionalProductSelectOption = () => {
    if (isPaymentDone === true) {
      return;
    }

    if (optionalQuotes && optionalQuotes.length > 0) {
      return optionalQuotes?.map((item: LinkedProduct, i) => (
        <div key={`optional-quote-${i}`}>
          <Switch
            checked={isLinkedOptionalQuoteSelected(productId, item.typeId)}
            onChange={() => onLinkedOptionalQuoteChange(productId, item.typeId)}
            inputProps={{ 'aria-label': 'controlled' }}
          />
          <span>
            {t('renewals-quote.optional-quotes-text', {
              productName: item.name.toLowerCase()
            })}
          </span>
          {!isLinkedOptionalQuoteSelected(productId, item.typeId) &&
            renderFormattedMonthlyOptionalLinkedProductPrice(item)}
        </div>
      ));
    }
  };

  const isSelectedQuote = () => {
    return productId === selectedQuote;
  };

  const getCardClass = () => {
    const isSelected = isSelectedQuote();
    const str = `flex-row osp-card ${isSelected ? 'selected ' : ''}quote-card ${
      showSelectOption === true ? 'cursor-pointer' : 'cursor-default'
    } cms-card`;

    return str;
  };

  const getMonthlyLinkedProductPrice = (linkedProduct: LinkedProduct) => {
    const installmentPrice = getInstalmentPrice(
      linkedProduct.price,
      noOfInstallments,
      linkedProduct.paymentMethod
    );

    const linkedProductPrice =
      installmentPrice.subsequentInstallmentPrice < 1
        ? undefined
        : installmentPrice.firstInstallmentPrice;

    return linkedProductPrice;
  };

  const renderFormattedMonthlyOptionalLinkedProductPrice = (
    linkedOptionalQuote: LinkedProduct
  ) => {
    const linkedOptionalQuotePrice =
      getMonthlyLinkedProductPrice(linkedOptionalQuote);

    return (
      <>
        {linkedOptionalQuotePrice !== undefined && linkedOptionalQuotePrice && (
          <p className='label bold'>
            {t('renewals-quote.monthly-optional-linked-product-price', {
              currencySymbol: currencySymbol,
              linkedOptionalQuotePrice: formatNumber(
                linkedOptionalQuotePrice,
                2,
                culture
              )
            })}
          </p>
        )}
      </>
    );
  };

  const renderPaymentOptions = () => {
    return productVariants
      .sort(function (a, b) {
        return (
          paymentMethodSortOrder.indexOf(a.paymentMethod) -
          paymentMethodSortOrder.indexOf(b.paymentMethod)
        );
      })
      .map((variant: ProductVariant, i) => {
        return (
          <>
            <div className='flex-row m1-top' key={`payment-${i}`}>
              <label className='inline-flex-style bold'>
                <div className='li-style-div'>
                  <FontAwesomeIcon icon={faCircle} size='xs' />
                </div>
                {variant.paymentMethod == PaymentMethod.Single &&
                  renderSinglePay(variant)}
                {(variant.paymentMethod == PaymentMethod.Monthly ||
                  variant.paymentMethod == PaymentMethod.PaymentAssist) &&
                  renderMonthlyPay(variant)}
                {variant.paymentMethod == PaymentMethod.Subscription &&
                  renderSubscriptionPay(variant)}
              </label>
            </div>
            <div className='subtitle-style'>
              {variant.paymentMethod == PaymentMethod.Single &&
                renderSinglePaySubTitle(variant)}
              {(variant.paymentMethod == PaymentMethod.Monthly ||
                variant.paymentMethod == PaymentMethod.PaymentAssist) && (
                <>
                  {renderMonthlyPaySubTitle(variant)}
                  <br />
                  {renderMonthlyTotal(variant)}
                </>
              )}
              {variant.paymentMethod == PaymentMethod.Subscription &&
                renderSubscriptionPaySubTitle(variant)}
            </div>
          </>
        );
      });
  };

  const renderMonthlyPay = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      productId,
      variant,
      noOfInstallments,
      selectedOptionalLinkedQuotes ?? [],
      isVariablePrice
    );
    if (isPaymentDone) {
      if (
        installmentPrice.subsequentInstallmentPrice !==
        installmentPrice.firstInstallmentPrice
      ) {
        return t('renewals-quote.inequal-payment-text', {
          currencySymbol: currencySymbol,
          firstInstallment: formatNumber(
            installmentPrice.firstInstallmentPrice,
            2,
            culture
          ),
          subsequentPrice: formatNumber(
            installmentPrice.subsequentInstallmentPrice,
            2,
            culture
          ),
          noOfInstallments: noOfInstallments - 1
        });
      }

      return t('equal-monthly-payment-text', {
        currencySymbol: currencySymbol,
        noOfInstallments: noOfInstallments,
        subsequentInstallmentPrice: formatNumber(
          installmentPrice.subsequentInstallmentPrice,
          2,
          culture
        )
      });
    }
    return monthlyInstallmentPlanText;
  };
  const renderMonthlyPaySubTitle = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      productId,
      variant,
      noOfInstallments,
      selectedOptionalLinkedQuotes ?? [],
      isVariablePrice
    );

    if (
      installmentPrice.subsequentInstallmentPrice !==
      installmentPrice.firstInstallmentPrice
    ) {
      return t('renewals-quote.monthly-pay-sub-title-text', {
        currencySymbol: currencySymbol,
        firstInstallmentPrice: formatNumber(
          installmentPrice.firstInstallmentPrice,
          2,
          culture
        ),
        noOfInstallments: noOfInstallments - 1,
        subsequentInstallmentPrice: formatNumber(
          installmentPrice.subsequentInstallmentPrice,
          2,
          culture
        )
      });
    }
    return t('renewals-quote.equal-monthly-pay-subtitle', {
      currencySymbol: currencySymbol,
      noOfInstallments: noOfInstallments,
      subsequentPrice: formatNumber(
        installmentPrice.subsequentInstallmentPrice,
        2,
        culture
      )
    });
  };

  const renderMonthlyTotal = (variant: ProductVariant) => {
    const variantTotalPrice = getVariantTotalPrice(isVariablePrice, variant);
    return t('renewals-quote.variant-total', {
      currencySymbol: currencySymbol,
      variantTotalPriceValue: formatNumber(variantTotalPrice ?? 0, 2, culture)
    });
  };

  const renderSinglePay = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      productId,
      variant,
      noOfInstallments,
      selectedOptionalLinkedQuotes ?? [],
      isVariablePrice
    );
    if (isPaymentDone) {
      return t('renewals-quote.single-pay-text', {
        currencySymbol: currencySymbol,
        firstInstallmentPrice: formatNumber(
          installmentPrice.firstInstallmentPrice,
          2,
          culture
        )
      });
    }
    return singlePayText;
  };
  const renderSinglePaySubTitle = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      productId,
      variant,
      noOfInstallments,
      selectedOptionalLinkedQuotes ?? [],
      isVariablePrice
    );

    return t('renewals-quote.single-pay-subtitle', {
      currencySymbol: currencySymbol,
      firstInstallmentPrice: formatNumber(
        installmentPrice.firstInstallmentPrice,
        2,
        culture
      )
    });
  };

  const renderSubscriptionPay = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      productId,
      variant,
      noOfInstallments,
      selectedOptionalLinkedQuotes ?? [],
      isVariablePrice
    );
    if (isPaymentDone) {
      return t('renewals-quote.subscription-pay-text', {
        currencySymbol: currencySymbol,
        firstInstallmentPrice: formatNumber(
          installmentPrice.firstInstallmentPrice,
          2,
          culture
        )
      });
    }
    return subscriptionPlanText;
  };

  const renderSubscriptionPaySubTitle = (variant: ProductVariant) => {
    const installmentPrice = getVariantInstalmentTotalPrice(
      productId,
      variant,
      noOfInstallments,
      selectedOptionalLinkedQuotes ?? [],
      isVariablePrice
    );

    return t('renewals-quote.subscription-pay-subtitle', {
      currencySymbol: currencySymbol,
      firstInstallmentPrice: formatNumber(
        installmentPrice.firstInstallmentPrice,
        2,
        culture
      )
    });
  };

  return (
    <div
      id={`renewal-card-${id}`}
      key={productId}
      onClick={(event) => onChange(event, productId)}>
      <div className={getCardClass()} data-testid='quote-card'>
        <div className='flex-column'>
          <div className='flex-row'>
            <div className='flex-column'>
              <div className='m2-bottom'>
                <h4>{quoteTitle}</h4>
                <div>{renderMandatoryQuoteIncludedText()}</div>
              </div>
              {showPaymentOptions ? (
                renderPaymentOptions()
              ) : (
                <div className='quote-card-price m2-bottom cms-quote-card-price'>
                  {renderPrice()}
                </div>
              )}
              <div>{renderOptionalProductSelectOption()}</div>
            </div>
          </div>
        </div>

        {hideBenefits !== true && (
          <div className='flex-column benefits-border benefits-text'>
            {renderBenefitSections()}
            {showMoreInfo === true && (
              <div>
                <ButtonCommand
                  name='btn-more-info'
                  onClick={onShowMore}
                  text={showMoreBtnLabel}
                  additionalClassNames='small'
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RenewalsQuoteCard;
